import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { apiError } from "../lib/api";
import { Activity } from "lucide-react";
import PasswordField from "../components/PasswordField";

export default function Login() {
  const { t, lang } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const passwordRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password);
      if (user.must_change_password) nav("/change-password");
      else if (user.role === "player") nav("/today");
      else if (user.role === "care_manager") nav("/care");
      else if (user.role === "accounting") nav("/accounting");
      else nav("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail === "Invalid email or password" ? t("invalidCredentials") : apiError(detail) || t("invalidCredentials"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pc-black)" }}>
      <div className="flex justify-between items-center px-6 py-6 safe-top">
        <div className="flex items-center gap-2">
          <Activity size={22} color="var(--pc-cyan)" />
          <span className="font-display font-bold text-lg">{t("appName")}</span>
        </div>
        <LanguageSwitcher />
      </div>
      <div className="flex-1 flex items-start justify-center px-6 pt-8 pb-16 sm:items-center sm:pt-0">
        <div className="w-full max-w-md pc-fade">
          <p className="pc-chip" style={{ background: "var(--pc-cyan-dim)", color: "var(--pc-cyan)" }}>{t("welcome")}</p>
          <h1 className="text-4xl sm:text-5xl font-display font-extrabold mt-5 leading-tight">{t("appName")}</h1>
          <p className="mt-3 text-base" style={{ color: "var(--pc-muted)" }}>{t("tagline")}</p>
          {location.state?.passwordReset && <div data-testid="password-reset-success" role="status" className="pc-chip mt-5" style={{ background: "var(--pc-cyan-dim)", color: "var(--pc-cyan)", textTransform: "none", fontSize: 13 }}>{t("resetComplete")}</div>}
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm" style={{ color: "var(--pc-muted)" }}>{t("email")}</label>
              <input data-testid="login-email" className="pc-input mt-2" type="email" autoComplete="username" enterKeyHint="next"
                value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); passwordRef.current?.focus(); passwordRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }); } }} required placeholder="name@club.com" />
            </div>
            <div>
              <label className="text-sm" style={{ color: "var(--pc-muted)" }}>{t("password")}</label>
              <PasswordField inputRef={passwordRef} data-testid="login-password" autoComplete="current-password" enterKeyHint="go" lang={lang}
                value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="pc-chip" style={{ background: "rgba(255,80,80,0.12)", color: "#ff8080", textTransform: "none", fontSize: 13 }}>{error}</div>}
            <button data-testid="login-submit" type="submit" className="pc-btn pc-btn-primary w-full" disabled={loading}>
              {loading ? t("signingIn") : t("signIn")}
            </button>
          </form>
          <button data-testid="forgot-link" onClick={() => nav("/forgot-password")} className="mt-5 text-sm"
            style={{ color: "var(--pc-cyan)", background: "none", border: "none", cursor: "pointer" }}>
            {t("forgotPassword")}
          </button>
        </div>
      </div>
    </div>
  );
}
